import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Clock, Save, XCircle, ChevronLeft, ChevronRight
} from 'lucide-react';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = [];
for (let h = 8; h <= 20; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, '0')}:00`);
  if (h < 20) TIME_SLOTS.push(`${String(h).padStart(2, '0')}:30`);
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const getMonthDays = (year, month) => {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days = [];
  const startDay = first.getDay() === 0 ? 6 : first.getDay() - 1;
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(d);
  return days;
};

/* ── Sub-component: DaySlotGrid ── */
const DaySlotGrid = ({ day, slots, onToggleSlot, onSetFullDay, onClearDay }) => {
  const hasSlots = slots.length > 0;
  return (
    <div className={`border p-4 transition-colors ${hasSlots ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100'}`} data-testid={`day-${day.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[#0F172A]">{day}</span>
        <div className="flex gap-1">
          <button onClick={() => onSetFullDay(day)} className="text-[10px] text-emerald-600 hover:underline font-medium" data-testid={`all-day-${day.toLowerCase()}`}>All</button>
          <span className="text-slate-300">|</span>
          <button onClick={() => onClearDay(day)} className="text-[10px] text-red-400 hover:underline font-medium" data-testid={`clear-${day.toLowerCase()}`}>Clear</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {TIME_SLOTS.map(time => (
          <button
            key={time}
            onClick={() => onToggleSlot(day, time)}
            className={`px-2 py-1 text-[10px] font-mono font-medium transition-all ${
              slots.includes(time)
                ? 'bg-[#E07A3A] text-white'
                : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
            }`}
            data-testid={`slot-${day.toLowerCase()}-${time}`}
          >
            {time}
          </button>
        ))}
      </div>
      {hasSlots && (
        <p className="text-[10px] text-emerald-600 mt-2 font-medium">{slots.length} slot{slots.length > 1 ? 's' : ''} selected</p>
      )}
    </div>
  );
};

/* ── Sub-component: WeeklySchedule ── */
const WeeklySchedule = ({ weeklySlots, onToggleSlot, onSetFullDay, onClearDay, onSave, saving }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] font-semibold">Weekly Availability</h3>
        <p className="text-xs text-slate-400 mt-1">Set your regular working hours. Clients will see these when booking.</p>
      </div>
      <Button onClick={onSave} disabled={saving} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-widest uppercase font-semibold px-6" data-testid="save-availability-btn">
        {saving ? <Clock className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Save</>}
      </Button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {DAYS.map(day => (
        <DaySlotGrid
          key={day}
          day={day}
          slots={weeklySlots[day] || []}
          onToggleSlot={onToggleSlot}
          onSetFullDay={onSetFullDay}
          onClearDay={onClearDay}
        />
      ))}
    </div>
  </div>
);

/* ── Sub-component: BlockedDatesCalendar ── */
const BlockedDatesCalendar = ({ blockedDates, onToggleDate }) => {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const monthDays = getMonthDays(viewYear, viewMonth);
  const sortedBlockedDates = useMemo(() => [...blockedDates].sort(), [blockedDates]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  return (
    <div>
      <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] font-semibold mb-1">Block Specific Dates</h3>
      <p className="text-xs text-slate-400 mb-4">Click on dates to mark them as unavailable (holidays, leave, etc.)</p>

      <div className="max-w-sm mx-auto border border-slate-100 p-5" data-testid="blocked-dates-calendar">
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="p-1.5 hover:bg-slate-50 transition-colors" data-testid="cal-prev">
            <ChevronLeft className="h-4 w-4 text-slate-400" />
          </button>
          <span className="text-sm font-semibold text-[#0F172A]">{MONTHS[viewMonth]} {viewYear}</span>
          <button onClick={nextMonth} className="p-1.5 hover:bg-slate-50 transition-colors" data-testid="cal-next">
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => (
            <div key={d} className="text-center text-[10px] text-slate-400 font-semibold py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {monthDays.map((day, i) => {
            if (!day) return <div key={`empty-${i}`} />;
            const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isBlocked = blockedDates.includes(dateStr);
            const isToday = dateStr === todayStr;
            const isPast = new Date(dateStr) < new Date(todayStr);
            return (
              <button
                key={dateStr}
                onClick={() => !isPast && onToggleDate(dateStr)}
                disabled={isPast}
                className={`aspect-square flex items-center justify-center text-xs font-medium transition-all ${
                  isBlocked ? 'bg-red-500 text-white'
                    : isToday ? 'bg-[#E07A3A] text-white'
                    : isPast ? 'text-slate-200 cursor-not-allowed'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`cal-date-${dateStr}`}
              >
                {day}
              </button>
            );
          })}
        </div>

        {blockedDates.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-2">Blocked dates ({blockedDates.length})</p>
            <div className="flex flex-wrap gap-1">
              {sortedBlockedDates.map(d => (
                <span key={d} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 text-[10px] font-medium">
                  {d}
                  <button onClick={() => onToggleDate(d)} className="hover:text-red-800"><XCircle className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Main Component ── */
const AvailabilityCalendar = () => {
  const [weeklySlots, setWeeklySlots] = useState({});
  const [blockedDates, setBlockedDates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAvailability = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/availability`);
      if (res.data.weekly_slots) setWeeklySlots(res.data.weekly_slots);
      if (res.data.blocked_dates) setBlockedDates(res.data.blocked_dates);
    } catch (err) { logger.error('Load availability:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const toggleSlot = (day, time) => {
    setWeeklySlots(prev => {
      const daySlots = prev[day] || [];
      return {
        ...prev,
        [day]: daySlots.includes(time) ? daySlots.filter(t => t !== time) : [...daySlots, time].sort()
      };
    });
  };

  const toggleBlockedDate = (dateStr) => {
    setBlockedDates(prev =>
      prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
    );
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/availability`, { weekly_slots: weeklySlots, blocked_dates: blockedDates });
      toast.success('Availability saved');
    } catch (err) {
      toast.error('Failed to save availability');
    }
    setSaving(false);
  };

  const setFullDay = (day) => {
    setWeeklySlots(prev => ({ ...prev, [day]: TIME_SLOTS.slice() }));
  };

  const clearDay = (day) => {
    setWeeklySlots(prev => ({ ...prev, [day]: [] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="availability-calendar">
      <WeeklySchedule
        weeklySlots={weeklySlots}
        onToggleSlot={toggleSlot}
        onSetFullDay={setFullDay}
        onClearDay={clearDay}
        onSave={saveAvailability}
        saving={saving}
      />
      <BlockedDatesCalendar
        blockedDates={blockedDates}
        onToggleDate={toggleBlockedDate}
      />
    </div>
  );
};

export default AvailabilityCalendar;
